import React, { useState, useEffect } from 'react';
import { Box, Typography, Radio, RadioGroup, Stack, IconButton, TextField, Alert } from '@mui/material';
import { DatePicker } from 'antd';
import { styled } from '@mui/material/styles';
import { BsToggleOnIcon, CalendarIcon, ToggleOffIcon } from '../../../../../Components/Icons/Icons';
import { getColorSettings } from '../../../../../Utils/helper/commonUtils';
import { AppColors } from '../../../../../Utils/helper/AppColors';

const { RangePicker } = DatePicker;

// Map recurrence type numbers to labels
const TYPE_TO_LABEL = {
	1: 'Daily',
	7: 'Weekly',
	14: 'Bi-Weekly',
	30: 'Monthly',
	0: 'Custom Date Range',
};

// Map labels to recurrence type numbers
const LABEL_TO_TYPE = {
	Daily: 1,
	Weekly: 7,
	'Bi-Weekly': 14,
	Monthly: 30,
	'Custom Date Range': 0,
};

const recurrenceOptions = [
	{ label: 'Daily', value: 'Campaign runs every day' },
	{ label: 'Weekly', value: 'Campaign runs once per week' },
	{ label: 'Bi-Weekly', value: 'Campaign runs every two weeks' },
	{ label: 'Monthly', value: 'Campaign runs once per month' },
	{ label: 'Custom Date Range', value: 'Set specific dates for campaign execution' },
];

const StyledFormControlLabel = styled('label')(({ theme, checked, prop }) => ({
	border: `1px solid ${AppColors.bg_gray || theme.quinary || '#E0E0E0'}`,
	borderRadius: '10px',
	padding: '8px 0px 8px 2px',
	textAlign: 'start',
	backgroundColor: checked ? AppColors.orange || theme.quinary || '#F3F0FF' : '#F9F9F9',
	color: checked ? AppColors.white || theme.fontColor || '#683FC7' : '#35373F',
	height: '75px',
	transition: 'all 0.3s ease',
	cursor: 'pointer',
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'start',
	boxSizing: 'border-box',
	'&:hover': {
		backgroundColor: checked ? AppColors.orange || theme.quinary || '#F3F0FF' : '#fafafa',
	},
	'& .MuiTypography-root': {
		color: checked ? AppColors.white || theme.fontColor || '#683FC7' : '#35373F',
		transition: 'color 0.3s ease',
	},
	...prop,
}));


export default function RecurrenceScheduleComponent({ details, setDetails, openOptions = true }) {
	// Initialize recurrence state from details or default to 'Weekly'
	const [recurrence, setRecurrence] = useState(() => {
		const type = details?.advancedOptions?.recurrence?.type;
		const customDays = details?.advancedOptions?.recurrence?.customDays;
		return type ? TYPE_TO_LABEL[type] : customDays ? 'Custom Date Range' : 'Daily';
	});

	// Initialize customDays from details
	const [customDays, setCustomDays] = useState(details?.advancedOptions?.recurrence?.customDays || '');

	// Update details when recurrence or customDays change
	useEffect(() => {
		updateDetails();
	}, [recurrence, customDays]);

	// Function to update details with current recurrence settings
	const updateDetails = () => {
		const type = recurrence === 'Custom Date Range' ? null : LABEL_TO_TYPE[recurrence];
		const newDetails = {
			...details,
			advancedOptions: {
				...(details?.advancedOptions || {}),
				recurrence: {
					type : recurrence === 'Custom Date Range' ? null : type,
					customDays: recurrence === 'Custom Date Range' ? type === null ? Number(customDays) || null : null : null,
				},
			},
		};
		setDetails(newDetails);
	};

	// Handle recurrence option selection
	const handleRecurrenceChange = (value) => {
		if (value === 'Custom Date Range') {
			setCustomDays(null); // Reset custom days when switching to Custom Date Range
		}
		setRecurrence(value);
	};

	// Handle custom days input
	const handleCustomDaysChange = (e) => {
		const value = e.target.value;

    
    // Remove any non-digit characters
    const cleanValue = value.replace(/\D/g, '');

    
    if (cleanValue === '' || ( parseInt(cleanValue) > 0 && parseInt(cleanValue) <= 365 )) {
        setCustomDays(cleanValue);
    }
	};

	const localSettingColors = getColorSettings();

	return (
		<Box mt={2} sx={{ borderRadius: 2 }}>
			{/* Recurrence Schedule */}
			{openOptions && (
				<>
					<Typography
						sx={{
							fontWeight: 500,
							fontSize: 16,
							// mb: 1,
						}}
					>
						Recurrence Schedule
					</Typography>

					{/* Info message below heading */}
					<Alert severity="info" sx={{ mb: 1, fontSize: 14 , padding: 0 , background : 'none' , '& .MuiAlert-icon': { color: '#42a5f5' , marginRight: 1 } }}>
						Specify the frequency at which the system will identify newly added employees and automatically enroll them into this campaign.
					</Alert>

					{/* Using RadioGroup as requested */}
					<Box sx={{ width: '100%' }}>
						<RadioGroup
							aria-label="recurrence"
							name="recurrence"
							value={recurrence}
							onChange={(e) => handleRecurrenceChange(e.target.value)}
							style={{
								display: 'flex',
								flexWrap: 'wrap',
								flexDirection: 'row',
								gap: '16px',
							}}
						>
							{recurrenceOptions.map((option) => (
								<Box key={option.label} width={{ xs: '100%', md: '45%' }}>
									<StyledFormControlLabel
										as="div"
										key={option.label}
										checked={recurrence === option.label}
										theme={localSettingColors}
										onClick={() => handleRecurrenceChange(option.label)}
									>
										<Radio
											sx={{
												'& .MuiSvgIcon-root': {
													fontSize: 28,
													color: recurrence === option.label ? AppColors.white || localSettingColors.primary : AppColors.midnight_black || localSettingColors.secondary,

												},
											}}
											value={option.label}
										/>
										<Stack direction={'column'} sx={{ textAlign: 'start' }}>
											<Typography sx={{ fontWeight: 600, fontSize: 16, fontStyle: 'semiBold', color: '#35373F' }}>
												{option.label}
											</Typography>
											<Typography sx={{ fontWeight: 400, fontSize: 14 }}>{option.value}</Typography>
										</Stack>
									</StyledFormControlLabel>
								</Box>
							))}

							{recurrence === 'Custom Date Range' && (
								<Box width={{ xs: '100%', md: '45%' }}>
									<StyledFormControlLabel
										as="div"
										key={'Custom Date Range Input'}
										checked={true}
										theme={localSettingColors}
										prop={{ paddingLeft: '16px', gap: '8px', backgroundColor: AppColors.orange || localSettingColors.quinary }}
									>
										<CalendarIcon size={22} color={'#545460'} />
										<TextField
											type="text"
											variant="outlined"
											value={customDays}
											onChange={handleCustomDaysChange}
											size="small"
											placeholder="Total days"
											error={recurrence === 'Custom Date Range' && !customDays}
											sx={(theme) => ({
												width: '50%',
												'& .MuiOutlinedInput-root': {
													backgroundColor: AppColors?.white || theme.palette.primary.lightest_shade,
													'& fieldset': {
														borderColor: AppColors?.orange || theme.palette.primary.lightest_shade,
														borderRadius: '1px',
													},
													'&:hover fieldset': {
														borderColor: AppColors?.orange || theme.palette.primary.lightest_shade,
													},
													'&.Mui-focused fieldset': {
														borderColor: AppColors?.orange || theme.palette.primary.darker_primary,
													},
													'& input': {
														backgroundColor: AppColors?.white || theme.palette.primary.lightest_shade,
													},
												},
											})}
										/>
									</StyledFormControlLabel>
									{recurrence === 'Custom Date Range' && !customDays && (
										<Typography sx={{ color: 'red', fontSize: '12px', mt: 1 }}>
											Please enter a valid number of days between 1 and 365.
										</Typography>
									)}
								</Box>
							)}
						</RadioGroup>
					</Box>
				</>
			)}
		</Box>
	);
}
